/**
 * Build Mode — shared types and the tracker stage vocabulary the UI renders.
 *
 * These mirror the wire shapes from `src/routes/build-mode.js`. The stage list and
 * labels are duplicated here rather than fetched because they are presentation:
 * the server sends the current stage and its label per request, and this list is
 * only used to lay out the full tracker rail (the steps a build will pass through).
 */

export const BUILD_STAGES = [
  'queued',
  'researching',
  'building',
  'security_check',
  'preview',
  'live',
] as const;

export type BuildStage = (typeof BUILD_STAGES)[number] | 'cancelled';

export const STAGE_LABELS: Record<string, string> = {
  queued: 'Queued',
  researching: 'Researching',
  building: 'Building',
  security_check: 'Security check',
  preview: 'Ready to preview',
  live: 'Live',
  cancelled: 'Cancelled',
};

export interface Capacity {
  pct_remaining: number;
  builds_included: number;
  denominator_units: number;
}

export type CapacityEntryKind = 'included' | 'build' | 'refund' | 'topup' | 'adjustment';

export interface CapacityEntry {
  kind: CapacityEntryKind;
  /** Signed percentage: negative for a spend, positive for a credit. */
  pct: number;
  reason: string;
  build_request_id: string | null;
  created_at: string;
}

export interface Quote {
  category: string;
  label: string;
  pct: number;
  estimate: string;
  affordable: boolean;
  accelerated: { pct: number; estimate: string };
}

export interface MessageResponse {
  replayed: boolean;
  reply: string | null;
  quote: Quote | null;
}

export interface StageEvent {
  stage: string;
  note: string | null;
  created_at: string;
}

export interface Feedback {
  id: string;
  body: string;
  resolved: boolean | number;
  created_at: string;
}

export interface ResearchQuestion { key: string; question: string; }
export interface ResearchState {
  build_request_id: string;
  category: string;
  answers: Record<string, string>;
  open_questions: ResearchQuestion[];
  complete: boolean;
}

export interface BuildRequest {
  id: string;
  request_text: string;
  /** "What building it surfaced" — the prose the review page opens with. */
  review_note?: string | null;
  stage: BuildStage;
  stage_label: string;
  stage_progress: number;
  delivery_estimate: string;
  is_terminal: boolean | number;
  cost_pct: number;
  accelerated: boolean | number;
  preview_flag_key: string | null;
  feature_enabled: boolean | number;
  promoted_at: string | null;
  archived_at: string | null;
  is_archived: boolean;
  created_at: string;
  shipped_at: string | null;
  stages: string[];
  stage_events?: StageEvent[];
  feedback?: Feedback[];
}

/** A stable client-side id for a chat message, so a resend dedupes server-side. */
export function newMessageId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ── Review rounds (§9, §10) ──────────────────────────────────────────────────────────

/**
 * Three states, and `pending` is a real stored value rather than the absence of one.
 * A nullable verdict cannot tell an unwalked screen from a screen someone looked at and
 * had no opinion on — and it lets a build ship on silence.
 */
export type Verdict = 'pass' | 'fail' | 'pending';

export const VERDICT_LABELS: Record<Verdict, string> = {
  pass: 'Pass',
  fail: 'Fail',
  pending: 'Pending',
};

/** What KIND of item this is, so the reviewer knows before they judge it. */
export type BuildItemState = 'already_built' | 'to_build' | 'shipped' | 'decision_data';

export const BUILD_STATE_LABELS: Record<BuildItemState, string> = {
  already_built: 'Already built',
  to_build: 'To build',
  shipped: 'Shipped',
  decision_data: 'Decision / data',
};

export interface ReviewItem {
  id: string;
  section: 'walk' | 'assumption' | 'reference';
  title: string;
  deep_link: string | null;
  current_state: string | null;
  plan: string | null;
  build_state: BuildItemState;
  built: boolean | number;
  verdict: Verdict;
  note: string | null;
  is_qa_target: boolean | number;
  claimed_by_user_id: string | null;
  claimed_at: string | null;
  position: number;
}

export interface ReviewRound {
  id: string;
  round_number: number;
  status: 'draft' | 'submitted';
  outcome: 'ship' | 'rework' | null;
  answers_snapshot: string | null;
  opened_at: string;
  submitted_at: string | null;
}

/**
 * Derived on the server so the counter strip and the send gate read the SAME
 * computation — a progress bar that disagrees with the button is how "why is send
 * greyed out" becomes a mystery.
 */
export interface ReviewSummary {
  total: number;
  /** Shown but not counted. Disclosed, because silent exclusion makes the bar lie. */
  excluded: number;
  built: number;
  pass: number;
  fail: number;
  pending: number;
  outcome: 'ship' | 'rework' | null;
  can_send: boolean;
}

export interface ReviewState {
  round: ReviewRound | null;
  items: ReviewItem[];
  summary: ReviewSummary;
}

/**
 * The send button's label states exactly what pressing it will do. One button, and its
 * effect is derived from the verdicts rather than chosen — so a reviewer can never ship
 * a failing round by picking the wrong action.
 */
export function sendLabel(summary: ReviewSummary): string {
  if (!summary.can_send) {
    // "Nothing to review yet" while the page is DISPLAYING items is a contradiction the
    // reviewer can see. A round can legitimately hold reference items and no QA checks —
    // there is something to read, just nothing to sign off, and only we can change that.
    if (summary.total === 0) {
      return summary.excluded > 0 ? 'Nothing here to sign off' : 'Nothing to review yet';
    }
    return `${summary.pending} item${summary.pending === 1 ? '' : 's'} still pending`;
  }
  return summary.outcome === 'ship' ? 'Send and ship it' : 'Send and send it back';
}
